import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Switch, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { UserApi } from '@/api';
import { DATE_FORMAT, TIME_FORMAT } from '@/config';
import { IUser } from '@/models';
import { DeviceNcicOriModal } from '@/pages/Employees/components/DeviceNcicOriModal';
import { DeviceNcicOriForm } from '@/pages/Employees/data';
import { useToastStore, ConfirmTypes } from '@/store';
import { colors } from '@/theme/variables';

const model = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.model !== 'n/a' ? 'Mobile Phone' : 'Browser',
});

const platform = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.platform || '-',
});

const manufacturer = (): Partial<GridColDef> => ({
  renderCell: (params) => `${params.row.manufacturer} - ${params.row.model}`,
});

const lastActive = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return params.row.lastActive
      ? `${moment(params.row.lastActive).format(DATE_FORMAT)} • ${moment(
          params.row.lastActive,
        ).format(TIME_FORMAT)}`
      : '-';
  },
});

const appName = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.appName ?? (params.row.fcmToken ? 'Native' : ''),
});

const appVersion = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.appVersion || '-',
});

const deviceStatus = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.deviceStatus || '-',
});

const deviceId = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.uid || '-',
});

const oriNumber = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return (
      <Typography
        sx={{
          bgcolor: colors.grey[10],
          borderRadius: 1,
          display: 'inline-block',
        }}
        width="full"
      >
        {params.row.oriNumber || '-'}
      </Typography>
    );
  },
});

const mnemonic = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return (
      <Typography
        sx={{
          bgcolor: colors.grey[10],
          borderRadius: 1,
          display: 'inline-block',
        }}
        width="full"
      >
        {params.row.mnemonic || '-'}
      </Typography>
    );
  },
});

const restrictDevice = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { updateToast } = useToastStore.getState();
    const isRestricted = params.row.isRestricted === true;

    const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRestrictionState = event.target.checked;
      try {
        await UserApi.restrictDevice(params.row._id, newRestrictionState);

        params.api.updateRows([
          { ...params.row, isRestricted: newRestrictionState },
        ]);

        updateToast({
          open: true,
          message: newRestrictionState
            ? 'Device has been restricted'
            : 'Device restriction has been removed',
          type: ConfirmTypes.SUCCESS,
        });
      } catch (err: any) {
        params.api.updateRows([{ ...params.row, isRestricted }]);

        updateToast({
          open: true,
          message: err.message,
          type: ConfirmTypes.ERROR,
        });
      }
    };

    return (
      <Tooltip
        title={
          isRestricted ? 'Unblock this device' : 'Block and logout this device'
        }
      >
        <Switch
          checked={isRestricted}
          onChange={handleToggle}
          color="error"
          inputProps={{ 'aria-label': 'restrict device toggle' }}
        />
      </Tooltip>
    );
  },
});

const isNCICEnabled = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { employeeId } = useParams();
    const [currentEmployee, setCurrentEmployee] = useState<IUser>();

    const [showNcicOriModal, setShowNcicOriModal] = useState(false);
    const { updateToast } = useToastStore.getState();
    const value = params.row.isNCICEnabled === true;

    useEffect(() => {
      fetchEmployeeData();
    }, [employeeId]);

    const handleModalOpen = () => {
      setShowNcicOriModal(true);
    };

    const handleModalClose = () => {
      setShowNcicOriModal(false);
    };

    const fetchEmployeeData = async () => {
      try {
        if (!employeeId) return;
        const res = await UserApi.getOne(employeeId);
        setCurrentEmployee(res.data);
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    };

    const handleSubmit = async (data: DeviceNcicOriForm) => {
      try {
        if (data.oriNumber || data.mnemonic) {
          await UserApi.updateDeviceNcic(
            params.row._id,
            data.oriNumber || '',
            data.mnemonic || '',
          );
        } else {
          await UserApi.toggleNcic(params.row._id);
        }
        params.api.updateRows([
          {
            ...params.row,
            isNCICEnabled: true,
            oriNumber: data.oriNumber || '',
            mnemonic: data.mnemonic || '',
          },
        ]);
        updateToast({
          open: true,
          message:
            'NCIC access has been enabled' +
            (currentEmployee?.stateId
              ? ''
              : `, but the employee profile is missing a State ID. Please update the profile`),
          type: ConfirmTypes.SUCCESS,
        });
        handleModalClose();
      } catch (err: any) {
        updateToast({
          open: true,
          message: err.message,
          type: ConfirmTypes.ERROR,
        });
      }
    };

    const handleToggle = async () => {
      if (value === true) {
        try {
          await UserApi.toggleNcic(params.row._id);
          params.api.updateRows([{ ...params.row, isNCICEnabled: !value }]);

          updateToast({
            open: true,
            message: !value
              ? 'NCIC access has been enabled' +
                (currentEmployee?.stateId
                  ? ''
                  : ', but the employee profile is missing a State ID. Please update the profile')
              : 'Device NCIC Search Access was turned off',
            type: ConfirmTypes.SUCCESS,
          });
        } catch (err: any) {
          params.api.updateRows([{ ...params.row, value }]);

          updateToast({
            open: true,
            message: err.message,
            type: ConfirmTypes.ERROR,
          });
        }
      } else {
        handleModalOpen();
      }
    };

    return (
      <Tooltip
        title={
          !value ? 'Grant NCIC Search Access' : 'Disable NCIC Search Access'
        }
      >
        <>
          <DeviceNcicOriModal
            ori={params.row.oriNumber}
            mne={params.row.mnemonic}
            open={showNcicOriModal}
            onClose={handleModalClose}
            onSubmit={handleSubmit}
          />
          <Switch
            disabled={params.row.isRestricted === true}
            checked={value}
            onChange={handleToggle}
            color="error"
            inputProps={{ 'aria-label': 'restrict device toggle' }}
          />
        </>
      </Tooltip>
    );
  },
});

export const employeeDevicesCols = () => {
  return {
    model,
    platform,
    manufacturer,
    lastActive,
    appName,
    appVersion,
    deviceStatus,
    restrictDevice,
    deviceId,
    oriNumber,
    mnemonic,
    isNCICEnabled,
  };
};
